/**
 * The info-dot look of a SingleSelect or MultiSelect option card (web only).
 *
 * An info dot is a button, and a button cannot sit inside the option's own
 * button. So an info-dot option draws the card as a plain box and stretches
 * the option's button over it as a transparent layer. The label, description
 * and indicator paint underneath that layer, so a press anywhere on them
 * still reaches the option. The dot is positioned, so it paints above the
 * layer and takes its own presses; it also stops them propagating.
 */

import React, { type ReactNode } from 'react';
import { InfoDot } from '../InfoDot/InfoDot.web';
import { INFO_DOT_DISC_SIZE } from '../InfoDot/InfoDot';
import {
  OPTION_BORDER_WIDTH,
  OPTION_CARD_RADIUS,
  type OptionTag as OptionTagValue,
  type SelectOption,
} from './SingleSelect';
import { OptionTag } from './optionTag.web';
import { spacing } from '../../elements/spacing';

export interface OptionInfoDotReveal {
  description: string;
  accessibilityLabel: string;
}

/**
 * The reveal an option asks for, or null when it should render as before:
 * no `info-dot` reveal, or a missing description or toggle label (a toggle
 * with no name, or nothing to reveal, would be worse than inline text).
 */
export function optionInfoDotReveal(option: SelectOption): OptionInfoDotReveal | null {
  if (option.descriptionReveal !== 'info-dot') return null;
  const description = option.description?.trim();
  const accessibilityLabel = option.infoDotAccessibilityLabel?.trim();
  if (!description || !accessibilityLabel) return null;
  return { description, accessibilityLabel };
}

/** The card box. The caller passes its own card look; this adds the layering. */
export function infoDotCardStyle(cardStyle: React.CSSProperties): React.CSSProperties {
  return { ...cardStyle, position: 'relative', cursor: 'default' };
}

/**
 * The option's button as a transparent layer over the whole card, border
 * included, so the focus ring draws around the card as it does today.
 */
export function infoDotOptionButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    position: 'absolute',
    inset: -OPTION_BORDER_WIDTH,
    margin: 0,
    padding: 0,
    border: 'none',
    borderRadius: OPTION_CARD_RADIUS,
    background: 'transparent',
    cursor: disabled ? 'default' : 'pointer',
  };
}

// The label flows as text and the dot rides on its last line, so a label that
// wraps keeps the dot beside its last word instead of at the column's edge.
const labelRowStyle: React.CSSProperties = {
  display: 'block',
};

// Room the dot takes at the end of the label's last line.
const DOT_SLOT_WIDTH = spacing.s + INFO_DOT_DISC_SIZE;

// The label's end padding reserves the dot's room on its last line, so the
// text wraps as if the dot were part of it and the dot can never land on a
// line of its own. The slot takes no room itself: it sits at the end of that
// padding and moves back over it, one label line tall with the dot centred.
function dotSlotStyle(lineHeight: React.CSSProperties['lineHeight']): React.CSSProperties {
  return {
    position: 'absolute',
    display: 'inline-flex',
    alignItems: 'center',
    height: lineHeight,
    width: DOT_SLOT_WIDTH,
    justifyContent: 'flex-end',
    transform: 'translateX(-100%)',
  };
}

interface OptionInfoDotContentProps {
  /** Analytics id for the dot; a screen passes its own, the generic one otherwise. */
  infoDotActionId?: string;
  label: string;
  /** The option's tag pill, drawn above the label. */
  tag?: OptionTagValue;
  reveal: OptionInfoDotReveal;
  expanded: boolean;
  descriptionId: string;
  onToggle: () => void;
  textContentStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  descriptionStyle: React.CSSProperties;
  'data-testid'?: string;
}

/**
 * Label with its dot, then the description. The description stays in the
 * page while closed (hidden) so the dot's `aria-controls` always names a
 * real element.
 */
export function OptionInfoDotContent({
  infoDotActionId = 'ui.info-dot.toggle',
  label,
  tag,
  reveal,
  expanded,
  descriptionId,
  onToggle,
  textContentStyle,
  labelStyle,
  descriptionStyle,
  'data-testid': testId,
}: OptionInfoDotContentProps) {
  return (
    <div style={textContentStyle}>
      {tag ? <OptionTag tag={tag} data-testid={testId ? `${testId}-tag` : undefined} /> : null}
      <span style={{ ...labelStyle, ...labelRowStyle }}>
        {/* The option button already carries this label as its name. */}
        <span style={{ ...labelStyle, paddingRight: DOT_SLOT_WIDTH }} aria-hidden="true">
          {label}
        </span>
        <span style={dotSlotStyle(labelStyle.lineHeight)}>
          <InfoDot
            actionId={infoDotActionId}
            accessibilityLabel={reveal.accessibilityLabel}
            expanded={expanded}
            controls={descriptionId}
            onToggle={onToggle}
            data-testid={testId ? `${testId}-info-dot` : undefined}
          />
        </span>
      </span>
      <span id={descriptionId} hidden={!expanded} style={descriptionStyle}>
        {reveal.description}
      </span>
    </div>
  );
}

interface OptionCardFrameProps {
  reveal: OptionInfoDotReveal | null;
  cardStyle: React.CSSProperties;
  /** The label, dot and description; drawn only for an info-dot option. */
  content: ReactNode;
  indicator: ReactNode;
  /** The option's own button. */
  children: ReactNode;
}

/**
 * Renders the option button as before, or, for an info-dot option, inside
 * the card box with its content and indicator painted beside it.
 */
export function OptionCardFrame({
  reveal,
  cardStyle,
  content,
  indicator,
  children,
}: OptionCardFrameProps) {
  if (!reveal) return <>{children}</>;
  return (
    <div style={infoDotCardStyle(cardStyle)}>
      {children}
      {content}
      {indicator}
    </div>
  );
}
